import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Zap } from "lucide-react";
import { CONTAINER_CLASS } from "../../constants/layout";
import { EppTricolorRingButton } from "../EppTricolorRingButton";
import { recordCampaignClick, recordContentEventClick } from "../../utils/campaignTracking";

function TrackedLink({ href, trackId, trackEntity, className, children, style }) {
  const onClick = () => {
    if (!trackId) return;
    if (trackEntity === "event") recordContentEventClick(trackId);
    else recordCampaignClick(trackId);
  };
  if (!href) return <span className={className} style={style}>{children}</span>;
  if (href.startsWith("http")) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} style={style} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className} style={style} onClick={onClick}>
      {children}
    </Link>
  );
}

const AUTO_INTERVAL = 6000;

/**
 * Builds pages so no page ever has only 1 item.
 * n=1 → [1], n=2 → [2], n=3 → [3], n=4 → [2,2],
 * n=5 → [3,2], n=6 → [2,2,2], n=7 → [3,2,2], n=8 → [2,2,2,2] …
 */
function buildPages(items) {
  const n = items.length;
  if (n <= 3) return [items.slice()];
  const pages = [];
  let i = 0;
  while (i < n) {
    const remaining = n - i;
    if (remaining <= 3) { pages.push(items.slice(i)); break; }
    pages.push(items.slice(i, i + 2));
    i += 2;
  }
  return pages;
}

function Card({ item }) {
  const accent = item.accent || "#190276";
  return (
    <div
      className="group rounded-2xl p-[2px] transition-shadow duration-500 hover:shadow-[0_8px_32px_-6px_rgba(25,2,118,0.35)]"
      style={{ background: "conic-gradient(from 90deg, #190276 0% 33%, #F2B531 33% 66%, #f7040a 66% 100%)" }}
    >
      <TrackedLink
        href={item.link}
        trackId={item.trackingId}
        trackEntity={item.trackingEntity}
        className="relative block h-[260px] overflow-hidden rounded-[14px] sm:h-[280px] md:h-[300px]"
      >
        {/* Full-bleed image */}
        {item.image ? (
          <img
            src={item.image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(135deg, ${accent}, ${accent}bb)` }}
          />
        )}

        {/* Double gradient — accent-tinted + readability */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: `linear-gradient(to top, ${accent}cc 0%, ${accent}55 30%, transparent 60%)` }}
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10" />

        {/* Top badges */}
        <div className="absolute left-4 top-4 flex flex-wrap items-center gap-2 sm:left-5 sm:top-5">
          {item.tag && (
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/15 px-3 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-white shadow-lg ring-1 ring-white/25 backdrop-blur-md sm:text-[11px]">
              {item.tag}
            </span>
          )}
          {item.urgencyPill && (
            <span className="rounded-lg bg-[#f7040a]/90 px-3 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-white shadow-lg ring-1 ring-white/25 backdrop-blur-md sm:text-[11px]">
              {item.urgencyPill}
            </span>
          )}
        </div>

        {/* Bottom — frosted glass panel */}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="mx-3 mb-3 rounded-xl border border-white/15 bg-white/10 px-4 py-3.5 backdrop-blur-xl sm:mx-4 sm:mb-4 sm:px-5 sm:py-4">
            {item.date && (
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/60 sm:text-[11px]">
                {item.date}
              </p>
            )}
            <h3 className="mb-1 font-heading text-[16px] font-black leading-tight tracking-tight text-white [text-shadow:0_1px_6px_rgba(0,0,0,0.4)] sm:text-lg md:text-xl">
              {item.title}
            </h3>
            {item.excerpt && (
              <p className="mb-3 text-[11px] leading-relaxed text-white/75 line-clamp-2 sm:text-[12px]">
                {item.excerpt}
              </p>
            )}
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-[11px] font-bold shadow-md transition-all group-hover:shadow-lg group-hover:scale-[1.03] sm:text-[12px]"
                style={{ color: accent }}
              >
                {item.ctaLabel || "Découvrir"} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </span>
              {/* Decorative accent dots */}
              <div className="hidden items-center gap-1 sm:flex" aria-hidden>
                <div className="h-1.5 w-1.5 rounded-full bg-[#190276]" />
                <div className="h-1.5 w-1.5 rounded-full bg-[#F2B531]" />
                <div className="h-1.5 w-1.5 rounded-full bg-[#f7040a]" />
              </div>
            </div>
          </div>
        </div>
      </TrackedLink>
    </div>
  );
}

export function SpotlightBanner({ items = [] }) {
  const pages = useMemo(() => buildPages(items), [items]);
  const totalPages = pages.length;
  const [page, setPage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  const goToPage = useCallback((i) => {
    setPage(i);
    setProgress(0);
    setIsAutoPlaying(false);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setIsAutoPlaying(true), 8000);
  }, []);

  const nextPage = useCallback(() => {
    setPage((p) => (p + 1) % totalPages);
    setProgress(0);
  }, [totalPages]);

  const prevPage = useCallback(() => {
    setPage((p) => (p - 1 + totalPages) % totalPages);
    setProgress(0);
    setIsAutoPlaying(false);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setIsAutoPlaying(true), 8000);
  }, [totalPages]);

  useEffect(() => {
    if (!isAutoPlaying || isPaused || totalPages <= 1) return;
    const interval = setInterval(nextPage, AUTO_INTERVAL);
    const progressInterval = setInterval(() => setProgress((p) => Math.min(p + 100 / 60, 100)), 100);
    return () => { clearInterval(interval); clearInterval(progressInterval); };
  }, [isAutoPlaying, isPaused, nextPage, totalPages]);

  useEffect(() => {
    setPage((p) => (totalPages > 0 ? p % totalPages : 0));
  }, [totalPages]);

  if (!items.length) return null;

  const pageItems = pages[page] || [];
  const cols = pageItems.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";

  return (
    <section className="bg-[#fafbfc] py-5 sm:py-6 md:py-8">
      <div className={CONTAINER_CLASS}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Section header — même style que les sections produits */}
          <div className="mb-4 flex items-center justify-between sm:mb-5">
            <div className="flex items-center gap-2.5">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg p-[2px] shadow-sm"
                style={{ background: "conic-gradient(from 90deg, #190276 0% 33%, #F2B531 33% 66%, #f7040a 66% 100%)" }}
              >
                <div className="flex h-full w-full items-center justify-center rounded-[6px] bg-[#190276]">
                  <Zap className="h-3.5 w-3.5 text-[#F2B531]" />
                </div>
              </div>
              <h2 className="font-heading text-base font-black tracking-tight text-gray-900 sm:text-lg">
                À ne pas manquer
              </h2>
            </div>
            {totalPages > 1 && (
              <span className="text-[11px] font-semibold text-gray-400 sm:text-[12px]">
                {page + 1} / {totalPages}
              </span>
            )}
          </div>

          {/* Carousel with hero-style navigation */}
          <div
            className="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={page}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className={`grid grid-cols-1 gap-3 ${cols} sm:gap-4`}
              >
                {pageItems.map((item) => (
                  <Card key={item.trackingId || item.title} item={item} />
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Tricolor ring arrows — identical to Hero */}
            {totalPages > 1 && (
              <>
                <div className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-[3.25rem] sm:block">
                  <div className="flex h-full items-center justify-center pl-1">
                    <div className="pointer-events-auto">
                      <EppTricolorRingButton
                        buttonClassName="h-10 w-10 sm:h-11 sm:w-11"
                        onClick={prevPage}
                        aria-label="Précédent"
                      >
                        <ChevronLeft className="h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]" strokeWidth={2.25} />
                      </EppTricolorRingButton>
                    </div>
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-[3.25rem] sm:block">
                  <div className="flex h-full items-center justify-center pr-1">
                    <div className="pointer-events-auto">
                      <EppTricolorRingButton
                        buttonClassName="h-10 w-10 sm:h-11 sm:w-11"
                        onClick={nextPage}
                        aria-label="Suivant"
                      >
                        <ChevronRight className="h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]" strokeWidth={2.25} />
                      </EppTricolorRingButton>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Progress bar — identical to Hero */}
          {totalPages > 1 && (
            <div className="mt-3 flex overflow-hidden rounded-full">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goToPage(i)}
                  className="relative h-1 flex-1"
                  aria-label={`Page ${i + 1}`}
                >
                  <div className="absolute inset-0 bg-[#190276]/15" />
                  {i === page && (
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-[#190276]"
                      style={{ width: `${progress}%` }}
                    />
                  )}
                  {i < page && <div className="absolute inset-0 bg-[#190276]/50" />}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
